/**
 * Per-column rules a project's values must satisfy — single source shared by
 * `ProjectFormModal` and the inline editors on `/projects`, which write the
 * same nine columns through the same endpoint and must refuse the same values
 * with the same sentences. Never stricter than the server: a client-side rule
 * the API doesn't share silently blocks saves that would otherwise succeed.
 *
 * `ProjectFormModal` and the inline editors on `/projects` write the same nine
 * columns through the same endpoint, so they must refuse the same values with
 * the same sentences. Two implementations of one rule agree on the day they are
 * written and drift on the first edit, and the drift is invisible: both screens
 * still look correct, and the one that is now wrong either refuses something the
 * API accepts or lets through something it does not.
 *
 * So the rules live here and both surfaces import them. Nothing in this module
 * is stricter than the server — a client-side rule the API does not share
 * rejects values that would have saved, which is the failure mode nobody
 * reports because it looks like the record simply cannot hold that value.
 *
 * ⚠️ **These are the WRITE keys, not the labels.** A 422's `path` names
 * `cityIds` and `regionIds`, never "Cities" and "Regions of focus" — which is
 * why `PROJECT_LABELS` is keyed on the write key and `PROJECT_WRITE_KEYS` is
 * derived from it rather than written out a second time.
 *
 * ⚠️ **CORRECTED 2026-08-29 (migration 095).** This paragraph read "`cityIds`
 * and `regionId`" and the map below carried `regionId: 'Region of focus'`. A
 * project's region became a SET — read `regions`, written `regionIds` — and
 * `regionId` is now a **422 naming the key** on a `.strict()` schema. Left
 * unchanged, `routeFieldErrors` would partition that refusal as `unroutable`:
 * it would vanish from beside the control that caused it while still inflating
 * the "one field needs attention, highlighted below" count.
 */

import { isRealDate } from '@/components/catalog/form-utils';
import { CAPACITY_FIELD_LABELS } from '@/lib/project-capacity';
import type { Project, ProjectProductInput } from '@/lib/types';

/** Every key `PATCH /projects/:id` accepts, mapped to what a person calls it. */
export const PROJECT_LABELS = {
  name: 'Project name',
  /**
   * ⚠️ **THREE write keys, ONE control on both surfaces.** `productId` is the
   * PRIMARY product (the `NOT NULL` column), `productIds` is the set, and
   * `products` is the set WITH each member's seeding tick; migrations 156 and
   * 190 kept all three. None is a label a reader ever sees on its own — the
   * caption is "Products" and a 422 naming any of them has to land on that one
   * control, which is why all three are in this map. See `projectProductsBody`
   * for why the first two travel together and `projectProductsPayload` for when
   * the third replaces the second.
   *
   * ⚠️ **`products` was MISSING here until 2026-09-14** — it shipped with the
   * tick on 2026-09-13 and was never added — so a 422 naming it was partitioned
   * as `unroutable`: it rendered in the form-level banner, highlighted nothing,
   * and still inflated the "one field needs attention, highlighted below" count.
   * The refusal it would carry is a bad `productId` INSIDE the array, which is
   * exactly the case a reader can fix at the control.
   */
  productId: 'Product',
  productIds: 'Products',
  products: 'Products',
  cycleId: 'Cycle',
  intakeId: 'Intake',
  startDate: 'Start date',
  endDate: 'End date',
  regionIds: 'Regions of focus',
  /**
   * ⚠️ DEPRECATED write key (owner 2026-10-27): `capacity` is now the derived
   * TOTAL of the two planned capacities, still accepted for ONE release and
   * mapped to `institutionCapacity`. Kept here only while the inline Capacity
   * cell on `/projects` still writes it; `ProjectFormModal` no longer sends it.
   */
  capacity: 'Capacity',
  ...CAPACITY_FIELD_LABELS,
  description: 'Description',
  cityIds: 'Cities',
  isActive: 'Active',
  isVirtualEvent: 'Virtual event',
} as const;

/**
 * Write-key list a 422 is routed against, derived from `PROJECT_LABELS` so it
 * can't drift from the controls. Used by `ApiError.routeFieldErrors` — a path
 * naming something outside this set binds to no control and shows as a
 * form-level message instead.
 *
 * ⚠️ `endDate` must stay in here even when a request never sends it: the
 * server checks `startDate <= endDate` against the POST-MERGE record, so a
 * body with only `startDate` can be refused at `endDate`.
 */
export const PROJECT_WRITE_KEYS: readonly string[] = Object.keys(PROJECT_LABELS);

/** Mirrors the server's limits exactly — no stricter, no looser. */
export const NAME_MAX = 240;
export const DESCRIPTION_MAX = 16000;
/** `capacity` is `INT UNSIGNED`. */
export const CAPACITY_MAX = 4294967295;
/** The server's own cap on the array: 200 entries, refused above that. */
export const CITIES_MAX = 200;
/** `MAX_PRODUCTS_PER_PROJECT` server-side — a payload guard, not a business rule. */
export const PRODUCTS_MAX = 50;

/** Whole and non-negative. `''`, `-1` and `1.5` are all 422s server-side. */
const UNSIGNED_INT = /^\d+$/;

/**
 * `name` is `NOT NULL` and trimmed 1..240 server-side, so an empty box isn't a
 * clear — returning a message here keeps an inline editor from sending `''`
 * and collecting a 422 for a field the user thought they were emptying.
 */
export function validateProjectName(value: string): string | null {
  const name = value.trim();
  if (name === '') return 'Enter a name for this project.';
  if (name.length > NAME_MAX) {
    return `${PROJECT_LABELS.name} must be ${NAME_MAX} characters or fewer.`;
  }
  return null;
}

/**
 * ⚠️ `''` and `'0'` are different answers and must never collapse together:
 * `''` is uncapped (`capacity: null` on the wire), `'0'` is a recorded
 * capacity of none. Tested on the STRING for that reason — through
 * `Number()`, `''` becomes `0` and "no ceiling" silently becomes "sold out".
 */
export function validateProjectCapacity(value: string): string | null {
  if (value === '') return null;
  if (!UNSIGNED_INT.test(value)) {
    return 'A whole number, 0 or more — empty means uncapped.';
  }
  if (Number(value) > CAPACITY_MAX) {
    return `Capacity must be ${CAPACITY_MAX.toLocaleString()} or less.`;
  }
  return null;
}

/**
 * The four planned-capacity / target write keys (owner 2026-10-27), in display
 * order: Institution row, then Partner row. Each is `INT UNSIGNED NULL`
 * server-side; `null` = not set, never 0.
 */
export const CAPACITY_PLAN_KEYS = [
  'institutionCapacity',
  'institutionTarget',
  'partnerCapacity',
  'partnerTarget',
] as const;

export type CapacityPlanKey = (typeof CAPACITY_PLAN_KEYS)[number];

/** The four figures as form strings: `''` = not set (`null`), `'0'` = zero. */
export type CapacityPlanStrings = Record<CapacityPlanKey, string>;

/**
 * Same string rules as `validateProjectCapacity` (whole, 0..CAPACITY_MAX, `''`
 * clears), with the sentence naming the field the reader typed into.
 */
export function validateCapacityPlanField(key: CapacityPlanKey, value: string): string | null {
  if (value === '') return null;
  const label = CAPACITY_FIELD_LABELS[key];
  if (!UNSIGNED_INT.test(value)) return `${label} must be a whole number, 0 or more.`;
  if (Number(value) > CAPACITY_MAX) {
    return `${label} must be ${CAPACITY_MAX.toLocaleString()} or less.`;
  }
  return null;
}

function figure(value: number | null): string {
  return value === null ? '' : String(value);
}

/**
 * A project's four figures as form strings.
 *
 * ⚠️ `capacityPlan` `undefined` is an OLDER API, not "nothing set": the single
 * legacy `capacity` is what migration 324 moves into Institution planned
 * capacity, so it seeds that field and the other three start empty.
 */
export function capacityPlanStrings(project: Project | null): CapacityPlanStrings {
  if (!project) {
    return { institutionCapacity: '', institutionTarget: '', partnerCapacity: '', partnerTarget: '' };
  }
  const plan = project.capacityPlan;
  if (plan === undefined) {
    return {
      institutionCapacity: figure(project.capacity),
      institutionTarget: '',
      partnerCapacity: '',
      partnerTarget: '',
    };
  }
  return {
    institutionCapacity: figure(plan.institution.capacity),
    institutionTarget: figure(plan.institution.target),
    partnerCapacity: figure(plan.partner.capacity),
    partnerTarget: figure(plan.partner.target),
  };
}

/**
 * The capacity half of a body. Create (`baseline: null`) sends only non-empty
 * keys; edit sends the diff with `''` -> `null`. Never sends `capacity`.
 */
export function capacityPlanBody(
  next: CapacityPlanStrings,
  baseline: CapacityPlanStrings | null,
): Partial<Record<CapacityPlanKey, number | null>> {
  const body: Partial<Record<CapacityPlanKey, number | null>> = {};
  for (const key of CAPACITY_PLAN_KEYS) {
    const value = next[key];
    if (baseline === null) {
      if (value !== '') body[key] = Number(value);
    } else if (value !== baseline[key]) {
      body[key] = value === '' ? null : Number(value);
    }
  }
  return body;
}

/**
 * One end of a project's period — `YYYY-MM-DD`, or empty.
 *
 * ⚠️ `''` is VALID and CLEARS (opposite of `validateProjectName`):
 * `startDate`/`endDate` are independently nullable, so an empty box means "no
 * date recorded," not an unusable value.
 *
 * `isRealDate` does both halves the server does (shape + calendar check, so
 * `2026-02-30` is refused here too) — imported rather than reimplemented to
 * avoid a second date validator drifting from this one.
 *
 * ⚠️ No start-vs-end comparison here: the server checks that against the
 * POST-MERGE record, but this function only sees one draft string and would
 * have to guess the other column's stored value — wrong for the ordinary case
 * of a one-sided inline edit. The 422 names `endDate` and states it in one go.
 *
 * ⚠️ No cycle-window check either: a project may legitimately run outside its
 * cycle's dates and the API never refuses one for it. A frontend-only
 * convention would make a legitimate project unsaveable with no explanation.
 */
export function validateProjectDate(value: string): string | null {
  if (value === '') return null;
  if (!isRealDate(value)) return 'Enter a real date, as YYYY-MM-DD.';
  return null;
}

export function validateProjectDescription(value: string): string | null {
  if (value.trim().length > DESCRIPTION_MAX) {
    return `${PROJECT_LABELS.description} must be ${DESCRIPTION_MAX.toLocaleString()} characters or fewer.`;
  }
  return null;
}

export function validateProjectCityIds(values: readonly string[]): string | null {
  if (values.length > CITIES_MAX) return `Choose ${CITIES_MAX} cities or fewer.`;
  return null;
}

/**
 * The products a project sells — migration 156, deployed 2026-09-09.
 *
 * ⚠️ **EMPTY IS REFUSED HERE, AND IT IS THE ONE SET ON THIS RECORD THAT IS.**
 * `cityIds` and `regionIds` both clear to nothing, so `[]` on those is a real
 * instruction. On `productIds` it is not: `d_projects.product_id` is `NOT NULL`
 * and the server re-asserts it into the set after every write, so
 * `productIds: []` means **"sell only the primary"** — the project keeps selling
 * something whatever the control says.
 *
 * That is why this refusal is not a client-side convention dressed as a server
 * rule, which this file's header forbids. The server enforces exactly this: a
 * project sells at least one product, structurally, and there is no request that
 * can say otherwise. Refusing an empty control is the only way for the screen to
 * agree with the record — the alternative is a multiselect the reader can empty,
 * a `200`, and a project that still sells the product they thought they removed.
 * **Nothing here ever sends `[]`, so that asymmetry is unreachable from this
 * app rather than merely avoided.**
 */
export function validateProjectProductIds(values: readonly string[]): string | null {
  if (values.length === 0) {
    return 'Choose at least one product. A project always sells something, so this cannot be emptied.';
  }
  if (values.length > PRODUCTS_MAX) return `Choose ${PRODUCTS_MAX} products or fewer.`;
  return null;
}

/**
 * The two product write keys, built together from ONE list of selected ids.
 *
 * ⚠️ **THEY TRAVEL AS A PAIR, and the shape of this function is what makes a
 * lone `productIds` inexpressible** — the same device as `cycleBody()` in
 * `ProjectInlineCells`, and for a closely related reason. `productIds` alone
 * cannot drop the primary: `replaceProducts` puts it back. So a reader who
 * removes the first chip and gets a `200` would watch it reappear, with nothing
 * having failed. Emitting `productId` from `values[0]` in the same body moves
 * the column first, and the set then stores **exactly what the reader sees**.
 *
 * Each half is still diffed on its own, because a PATCH must send only what
 * changed: re-asserting an untouched set that holds a since-retired member is a
 * 422 on a field nobody opened, and re-asserting an untouched `productId` is the
 * same trap the header of `ProjectUpdateInput` describes.
 *
 * The set compare is order-INSENSITIVE (the read comes back primary-first then
 * alphabetically, never in the order it was sent) while the primary compare is
 * positional — so re-picking the same products in a different order sends
 * `productId` alone, which is correct: the membership did not move, the primary
 * did.
 *
 * Pass `baseline: null` for a create, where both keys always go.
 */
export function projectProductsBody(
  next: readonly string[],
  baseline: readonly string[] | null,
): { productId?: number; productIds?: number[] } {
  const body: { productId?: number; productIds?: number[] } = {};
  const primary = next[0];

  if (baseline === null) {
    if (primary !== undefined) body.productId = Number(primary);
    body.productIds = next.map(Number);
    return body;
  }

  if (primary !== undefined && primary !== baseline[0]) body.productId = Number(primary);

  const seen = new Set(baseline);
  const sameSet = next.length === baseline.length && next.every((id) => seen.has(id));
  if (!sameSet) body.productIds = next.map(Number);

  return body;
}

/**
 * A product with no entry in a selection's flag map is pulled into a new
 * document's line items.
 *
 * It matches the COLUMN's own default (`seeds_line_items TINYINT(1) NOT NULL
 * DEFAULT 1`, migration 190), which is what keeps the tick additive: an
 * untouched project seeds exactly what it seeded before the control existed,
 * and a `false` anywhere is always somebody having deliberately unticked a box.
 */
export const SEEDS_LINE_ITEMS_DEFAULT = true;

/**
 * ⚠️ The ONE place the missing-entry default is applied, on either surface.
 *
 * The fallback is reachable only for a product just added to a draft — every
 * product the SERVER sent arrives with `seedsLineItems` already populated,
 * because `ProjectProductRef.seedsLineItems` is REQUIRED on the wire. So this
 * is not a `?? true` papering over an unread value; it is the answer for a
 * product that has no stored row yet.
 */
export function seedsLineItemsOf(
  flags: Readonly<Record<string, boolean>>,
  productId: string,
): boolean {
  return flags[productId] ?? SEEDS_LINE_ITEMS_DEFAULT;
}

/**
 * A project's chosen products and each one's seeding tick, as the two surfaces
 * that edit them both hold it.
 *
 * `ids` is PRIMARY FIRST — `replaceProducts` re-asserts the primary into the set
 * either way, so sending it anywhere but first stores a set that disagrees with
 * the chips on screen.
 */
export interface ProjectProductSelection {
  ids: readonly string[];
  /** ⚠️ A MISSING ENTRY MEANS ON. Read it through `seedsLineItemsOf`, never directly. */
  seedsLineItems: Readonly<Record<string, boolean>>;
}

/**
 * Has any CHOSEN product's tick moved away from the baseline?
 *
 * Walks `ids`, never the flag map, so a product that is no longer chosen cannot
 * report a change — dropping one is a MEMBERSHIP change and is diffed as one.
 *
 * ⚠️ **Merely ADDING a product is NOT a tick change**, which is why a create
 * (`baseline === null`) compares against the DEFAULT rather than against
 * nothing: a newly chosen product left ticked is exactly what the server stores
 * from `productIds` alone. Untick it and there is something to say.
 */
function productFlagsMoved(
  next: ProjectProductSelection,
  baseline: ProjectProductSelection | null,
): boolean {
  return next.ids.some(
    (id) =>
      seedsLineItemsOf(next.seedsLineItems, id) !==
      (baseline === null
        ? SEEDS_LINE_ITEMS_DEFAULT
        : seedsLineItemsOf(baseline.seedsLineItems, id)),
  );
}

/**
 * THE PRODUCT HALF OF EVERY BODY EITHER SURFACE SENDS — all three write keys and
 * the rule deciding which of them go. `ProjectFormModal` and the inline Products
 * cell on `/projects` both come through here and neither builds a product key of
 * its own.
 *
 * ⛔ **`productIds` AND `products` ARE MUTUALLY EXCLUSIVE: sending both in one
 * body is a 422 NAMING BOTH.** They are not two halves of a payload, they are
 * two spellings of one whole-set replace:
 *
 *   `productIds`  the MEMBERSHIP alone.
 *   `products`    the MEMBERSHIP **and** every member's tick, in one array.
 *
 * 🔴 **`productIds` DOES NOT LEAVE THE TICKS ALONE. IT RESETS EVERY ONE TO ON.**
 * Measured in `replaceProducts`: a set write **DELETEs every link row and
 * re-INSERTs** it, and a row re-inserted through `productIds` carries no flag, so
 * it takes the column default — which is ON. **There is no path on which the old
 * flags survive that DELETE.**
 *
 * So adding or removing ONE product switches seeding back on for every product
 * an administrator had switched off, with a `200`, no refusal, and nothing on
 * either screen saying so. The symptom lands weeks later on a document carrying
 * lines nobody chose.
 *
 * 🔴 **THAT IS NOT A HYPOTHETICAL: the inline Products cell on `/projects`
 * SHIPPED DOING EXACTLY THIS on 2026-09-13 and was fixed on 2026-09-14.** The
 * form got the rule right on the day and the list row did not, because the list
 * row already had `projectProductsBody` and nothing made it read the flag. That
 * is the whole reason this function lives here rather than inside either file.
 *
 * **Both surfaces therefore send `products` whenever EITHER the set or a tick
 * moved**, and both can: each loads every chosen product's flag, so re-asserting
 * them restates what it was given rather than inventing anything. This is the
 * one case where the re-assert-nothing-untouched habit is wrong — the whole-set
 * replace makes an omitted flag a **WRITE**, not an absence.
 *
 * ⚠️ **`productIds` is consequently UNREACHABLE from this app**, and it is kept
 * on the wire for callers that predate the flag. **Do not "simplify" this back to
 * a two-branch choice.**
 *
 * > ⚠️ **The trap is that `productIds` reads as the narrower, safer key and is
 * > the destructive one.** Narrower on the wire is not narrower in effect once
 * > the verb behind it is a replace — the omitted column is still written, just
 * > with a value nobody chose.
 *
 * ⚠️ **`productId` — the PRIMARY, the `NOT NULL` column — is in neither camp**
 * and rides along with whichever of the two goes. It has a diff of its own (the
 * primary MOVED, which a set compare cannot see); this rule is only about the set.
 *
 * The exclusivity is STRUCTURAL rather than checked: no path through this
 * function returns both keys, so no call site can forget it.
 *
 * Pass `baseline: null` for a create.
 */
export function projectProductsPayload(
  next: ProjectProductSelection,
  baseline: ProjectProductSelection | null,
): {
  productId?: number;
  productIds?: number[];
  /*
   * ⚠️ `ProjectProductInput`, not a structural literal and not
   * `ProjectProductRef`: you WRITE `productId`, you READ `id` and `name`, and
   * the write schema is `.strict()` — so round-tripping a read entry into this
   * array is a 422 naming `id`, never a silent strip. Naming the type is what
   * makes `tsc` say so at the `.map` below rather than at the server.
   */
  products?: ProjectProductInput[];
} {
  const { productId, productIds } = projectProductsBody(
    next.ids,
    baseline === null ? null : baseline.ids,
  );
  const primary = productId === undefined ? {} : { productId };

  /*
   * ⚠️ `productIds !== undefined` IS the "the set moved" test — `projectProductsBody`
   * emits the key only on a real membership diff — and it is read here purely as
   * that signal. The key itself is then DISCARDED rather than sent: see above,
   * where sending it resets every tick to ON.
   */
  const setMoved = productIds !== undefined;
  if (!setMoved && !productFlagsMoved(next, baseline)) return primary;

  return {
    ...primary,
    products: next.ids.map((id) => ({
      productId: Number(id),
      seedsLineItems: seedsLineItemsOf(next.seedsLineItems, id),
    })),
  };
}
